import { create } from 'zustand';

// Live field-report buffer (docs/19 §1). Fed by the report:new / report:acked
// socket events; the War Diary "Reports" tab merges this with its query snapshot
// (same pattern as ccStore.recentFeed + FeedList). Carries the live deltas that
// must drive the ACK siren immediately, without waiting for the next poll.

export type ReportType =
  | 'CONTACTREP' | 'SITREP' | 'SPOTREP' | 'CASEVAC'
  | 'LOCSTAT' | 'LOGREQ' | 'OBSTACLE' | 'FREETEXT';
export type ReportPriority = 'FLASH' | 'IMMEDIATE' | 'PRIORITY' | 'ROUTINE';
export type ReportStatus = 'RECEIVED' | 'ACKED';

export interface ReportAttachment {
  id: string;
  kind: 'PHOTO' | 'AUDIO';
  bytes: number;
  mime: string;
  createdAt: string;
}

export interface FieldReport {
  id: string;
  type: ReportType;
  priority: ReportPriority;
  status: ReportStatus;
  platoonId: string | null;
  unitId: string | null;
  deviceId?: string | null;
  lat?: number | null;
  lon?: number | null;
  mgrs?: string | null;
  payload: Record<string, unknown> | null;
  deviceTime?: string | null;
  createdAt: string;
  ackRequired: boolean;
  ackedById?: string | null;
  ackedAt?: string | null;
  attachments: ReportAttachment[];
  platoon?: { id: string; callsign: string; platoonNumber?: number; unitId?: string | null } | null;
}

const REPORT_CAP = 300;

interface ReportState {
  liveReports: FieldReport[];
  pushReport: (r: FieldReport) => void;
  applyAck: (r: FieldReport) => void;
  setLive: (rs: FieldReport[]) => void;
}

export const useReportStore = create<ReportState>((set) => ({
  liveReports: [],
  pushReport: (r) =>
    set((s) => {
      if (!r || !r.id) return s;
      return { liveReports: [r, ...s.liveReports.filter((x) => x.id !== r.id)].slice(0, REPORT_CAP) };
    }),
  applyAck: (r) =>
    set((s) => {
      if (!r || !r.id) return s;
      const exists = s.liveReports.some((x) => x.id === r.id);
      return {
        liveReports: exists
          ? s.liveReports.map((x) => (x.id === r.id ? { ...x, ...r } : x))
          : [r, ...s.liveReports].slice(0, REPORT_CAP),
      };
    }),
  setLive: (rs) => set({ liveReports: (rs || []).slice(0, REPORT_CAP) }),
}));

// A report demands audible attention while it is unacknowledged.
export function isUnackedUrgent(r: FieldReport): boolean {
  return r.status !== 'ACKED' && r.ackRequired && (r.priority === 'FLASH' || r.priority === 'IMMEDIATE');
}
